import DOMPurify from 'dompurify';

/**
 * Chuẩn bị HTML rich-text (mô tả topic, nội dung response) trước khi đưa vào
 * dangerouslySetInnerHTML.
 *
 * SANITIZE: nội dung do người dùng nhập, lưu server-side rồi hiện cho mọi phòng
 * ban — không lọc thì một <img onerror=…> là stored XSS. Lọc TRƯỚC khi gắn
 * thuộc tính ảnh bên dưới, để các thuộc tính đó không bị DOMPurify xoá.
 *
 * VÌ SAO CẦN XỬ LÝ ẢNH: description/response do TinyMCE sinh ra, ảnh đã upload
 * lên S3 nên mỗi bài có thể mang nhiều thẻ <img> URL thật. Một trang 10 topic là
 * hàng chục ảnh cùng tải một lúc ngay khi mở tab.
 *
 * Cách cũ (IntersectionObserver rồi fade opacity) KHÔNG tiết kiệm được byte nào:
 * thẻ <img> đã có src thật nên trình duyệt tải ngay lúc parse, observer chỉ chạy
 * sau đó và đổi opacity. Đây mới là thứ thật sự hoãn được request:
 *
 *   loading="lazy"   — trình duyệt bỏ qua ảnh ngoài viewport, native, không JS
 *   decoding="async" — decode ngoài main thread, không chặn cuộn
 *
 * DOMParser chỉ dựng cây DOM tách rời: không chạy script, không phát request.
 * Kết quả nên được memo hoá theo chuỗi HTML đầu vào.
 */
export const IMAGE_ZOOM_HINT = 'Click to enlarge';

// Như sanitizeHtml dùng chung, nhưng giữ target của link: TinyMCE "Open in new
// window" hay dùng cho link bản vẽ trên S3 — mất target thì bấm vào là rời SPA
// ngay trong tab, form trả lời đang gõ dở cũng mất. rel được ép lại bên dưới.
const sanitizeRichHtml = (html) =>
  DOMPurify.sanitize(String(html), { USE_PROFILES: { html: true }, ADD_ATTR: ['target'] });

export const prepareRichHtml = (html) => {
  if (!html) return '';

  const clean = sanitizeRichHtml(html);
  if (typeof window === 'undefined' || typeof window.DOMParser !== 'function') return clean;

  try {
    const doc = new window.DOMParser().parseFromString(clean, 'text/html');
    const images = doc.querySelectorAll('img');
    const newTabLinks = doc.querySelectorAll('a[target]');
    if (images.length === 0 && newTabLinks.length === 0) return clean;

    newTabLinks.forEach((link) => {
      if (link.getAttribute('target') === '_blank') {
        link.setAttribute('rel', 'noopener noreferrer');
      } else {
        link.removeAttribute('target');
      }
    });

    images.forEach((img) => {
      img.setAttribute('loading', 'lazy');
      img.setAttribute('decoding', 'async');
      // alt rỗng để screen reader bỏ qua ảnh trang trí thay vì đọc cả URL.
      if (!img.hasAttribute('alt')) img.setAttribute('alt', '');
      // RichContent mở lightbox khi bấm ảnh — con trỏ zoom-in chưa đủ để biết.
      if (!img.hasAttribute('title')) img.setAttribute('title', IMAGE_ZOOM_HINT);
    });

    return doc.body.innerHTML;
  } catch {
    // HTML hỏng thì trả bản đã lọc — thà mất lazy còn hơn mất nội dung.
    return clean;
  }
};

export default prepareRichHtml;
